import { generateHookExports } from 'api/generateExports';
import { createCRUDService } from 'api/queryService';
import axios from '../axios';
import { useMutation, useQuery } from '@tanstack/react-query';
import { API_CONFIG } from 'services/apiConfig';
import { showToast } from 'services/utils';
import { formatPayloadIfNeeded } from 'api/utils';
import { transformEquipmentData } from 'pages/Mam-of-equipments/utils/utils';

let baseData = API_CONFIG.MAM_OF_EQUIPMENT;
const BASE_URL = baseData?.path;
const QUERY_KEY = baseData?.queryKey;

const mamOfEquipmentService = createCRUDService({
  baseUrl: BASE_URL,
  queryKey: QUERY_KEY,
  custom: {
    updateSpecificForEquipment: () => {
      return useMutation({
        mutationFn: (payload) => {
          const data = formatPayloadIfNeeded(payload, payload?.isCustomImage ?? false);
          return axios.put(`${BASE_URL}/update-specific-for-equipment`, data, {
            headers: data instanceof FormData ? { 'Content-Type': 'multipart/form-data' } : {},
          })
            .then((res) => res.data);
        },
        onSuccess: (data) => {
          showToast(data);
        },
        onError: (error) => {
          showToast(error?.data);
        }
      });
    },
    getByFormatId: (formatId, params = {}, options = {}) => {
      return useQuery({
        queryKey: [QUERY_KEY, 'getByFormatId', formatId, params],
        queryFn: () => {
          return axios.get(`${BASE_URL}/get-by-format-id/${formatId}`, { params }).then((res) => res.data);
        },
        enabled: !!formatId,
        retry: options.retry ?? 1,
      });
    },
    createReport: () => {
      return useMutation({
        mutationFn: (payload) => {
          const data = formatPayloadIfNeeded(payload, payload?.isCustomImage ?? false);
          return axios.post(`${BASE_URL}/create-report`, data, {
            headers: data instanceof FormData ? { 'Content-Type': 'multipart/form-data' } : {},
          })
            .then((res) => res.data);
        },
        onSuccess: (data) => {
          showToast(data);
        },
        onError: (error) => {
          showToast(error?.data);
        }
      });
    },
    getEquipment: () => {
      return useMutation({
        mutationFn: (payload) => {
          const data = formatPayloadIfNeeded(payload, payload?.isCustomImage ?? false);
          return axios.post(`${BASE_URL}/get-equipment`, data, {
            headers: data instanceof FormData ? { 'Content-Type': 'multipart/form-data' } : {},
          })
            .then((res) => res.data);
        },
        onError: (error) => {
          showToast(error?.data);
        }
      });
    },
    updateCalibrationDate: () => {
      return useMutation({
        mutationFn: (payload) => {
          const data = formatPayloadIfNeeded(payload, payload?.isCustomImage ?? false);
          return axios.post(`${BASE_URL}/update-calibration-date`, data, {
            headers: data instanceof FormData ? { 'Content-Type': 'multipart/form-data' } : {},
          })
            .then((res) => res.data);
        },
        onSuccess: (data) => {
          showToast(data);
        },
        onError: (error) => {
          showToast(error?.data);
        }
      });
    },
    updateExternalDate: () => {
      return useMutation({
        mutationFn: (payload) => {
          const data = formatPayloadIfNeeded(payload, payload?.isCustomImage ?? false);
          return axios.put(`${BASE_URL}/update-external-date`, data, {
            headers: data instanceof FormData ? { 'Content-Type': 'multipart/form-data' } : {},
          })
            .then((res) => res.data);
        },
        onSuccess: (data) => {
          showToast(data);
        },
        onError: (error) => {
          showToast(error?.data);
        }
      });
    },
    createPlan: () => {
      return useMutation({
        mutationFn: (payload) => {
          const data = formatPayloadIfNeeded(payload, payload?.isCustomImage ?? false);
          return axios.post(`${BASE_URL}/create-plan`, data, {
            headers: data instanceof FormData ? { 'Content-Type': 'multipart/form-data' } : {},
          })
            .then((res) => res.data);
        },
      });
    },
    fetchByMasterDoc: (params = {}, options = {}) => {
      return useQuery({
        queryKey: [QUERY_KEY, 'fetchByMasterDoc', params],
        queryFn: async () => {
          const res = await axios.get(`${BASE_URL}/fetch-by-masterdoc`, { params });
          return transformEquipmentData(res?.data?.data?.document?.rows) ?? [];
        },
        retry: options.retry ?? 1,
        ...options,
      });
    }
  }
});


export const {
  mamOfEquipmentGetById,
  mamOfEquipmentDownload,
  mamOfEquipmentUpdate,
  mamOfEquipmentCreate,
  mamOfEquipmentList,
  mamOfEquipmentUpdateSpecificForEquipment,
  mamOfEquipmentGetByFormatId,
  mamOfEquipmentCreateReport,
  mamOfEquipmentGetEquipment,
  mamOfEquipmentUpdateCalibrationDate,
  mamOfEquipmentUpdateExternalDate,
  mamOfEquipmentCreatePlan,
  mamOfEquipmentFetchByMasterDoc
} = generateHookExports(mamOfEquipmentService, "mamOfEquipment");